import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { operatorsForType } from '../lib/operatorsForType';
import type { FilterOperator, QueryFilter } from '../types/query.types';

export interface QueryFilterRowProps {
  filter: QueryFilter;
  columns: { name: string; type: string }[];
  onChange: (next: QueryFilter) => void;
  onRemove: () => void;
  error?: string;
}

const NO_VALUE: FilterOperator[] = ['is null', 'is not null'];
const MULTI: FilterOperator[] = ['in', 'not in'];

// Display labels for operators whose wire value isn't self-explanatory (#877).
// The wire value (sent to the backend) is the map key; the label is shown in the UI.
const OPERATOR_LABELS: Partial<Record<FilterOperator, string>> = {
  contains: 'contains',
  icontains: 'contains (case-insensitive)',
};

export function QueryFilterRow({
  filter,
  columns,
  onChange,
  onRemove,
  error,
}: QueryFilterRowProps) {
  const colType = columns.find((c) => c.name === filter.column)?.type ?? '';
  const ops = operatorsForType(colType);

  const handleColumn = (column: string) => {
    const newType = columns.find((c) => c.name === column)?.type ?? '';
    const validOps = operatorsForType(newType);
    if (validOps.includes(filter.operator)) {
      // Operator still valid for the new column type — preserve the value.
      onChange({ ...filter, column });
      return;
    }
    // Operator invalid for the new type — reset to '=' and reset value.
    onChange({
      column,
      operator: '=' as FilterOperator,
      value: '',
    });
  };

  const handleOperator = (operator: FilterOperator) => {
    let value: unknown = '';
    if (NO_VALUE.includes(operator)) value = undefined;
    else if (operator === 'between') value = ['', ''];
    else if (MULTI.includes(operator)) value = [];
    onChange({ ...filter, operator, value });
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Select value={filter.column} onValueChange={handleColumn}>
          <SelectTrigger data-testid="filter-column" className="w-40">
            <SelectValue placeholder="Column" />
          </SelectTrigger>
          <SelectContent>
            {columns.map((c) => (
              <SelectItem key={c.name} value={c.name}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filter.operator}
          onValueChange={(v) => handleOperator(v as FilterOperator)}
        >
          <SelectTrigger data-testid="filter-operator" className="w-36">
            <SelectValue placeholder="Operator" />
          </SelectTrigger>
          <SelectContent>
            {ops.map((op) => (
              <SelectItem key={op} value={op}>
                {OPERATOR_LABELS[op] ?? op}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {NO_VALUE.includes(filter.operator) ? null : filter.operator ===
          'between' ? (
          <>
            <Input
              data-testid="filter-value-lo"
              className="w-24"
              placeholder="from"
              aria-invalid={error ? true : undefined}
              value={(filter.value as unknown[])?.[0] as string ?? ''}
              onChange={(e) =>
                onChange({
                  ...filter,
                  value: [
                    e.target.value,
                    (filter.value as unknown[])?.[1] ?? '',
                  ],
                })
              }
            />
            <Input
              data-testid="filter-value-hi"
              className="w-24"
              placeholder="to"
              aria-invalid={error ? true : undefined}
              value={(filter.value as unknown[])?.[1] as string ?? ''}
              onChange={(e) =>
                onChange({
                  ...filter,
                  value: [
                    (filter.value as unknown[])?.[0] ?? '',
                    e.target.value,
                  ],
                })
              }
            />
          </>
        ) : MULTI.includes(filter.operator) ? (
          <Input
            data-testid="filter-value"
            className="w-56"
            placeholder="comma,separated,values"
            aria-invalid={error ? true : undefined}
            value={
              Array.isArray(filter.value)
                ? (filter.value as string[]).join(',')
                : (filter.value as string) ?? ''
            }
            onChange={(e) =>
              onChange({
                ...filter,
                value: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
        ) : (
          <Input
            data-testid="filter-value"
            className="w-48"
            placeholder="value"
            aria-invalid={error ? true : undefined}
            value={(filter.value as string) ?? ''}
            onChange={(e) => onChange({ ...filter, value: e.target.value })}
          />
        )}

        <Button
          variant="ghost"
          size="icon"
          data-testid="filter-remove"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {error && (
        <p data-testid="filter-error" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
